import * as React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Button,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import AppLoader from '../../../../Components/AppLoader';
import { ProjectServices } from '../../../../Services/ProjectServices';
import { loadOnboardingReview } from './reviewData';

const projectServices = new ProjectServices();

const useStyles = makeStyles((theme) => ({
    pageIntro: {
        marginBottom: theme.spacing(2.5),
    },
    eyebrow: {
        color: theme.palette.primary.main,
        fontSize: '0.6875rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
    },
    title: {
        color: 'var(--dm-text-strong, #263548)',
        fontSize: '1.25rem',
        fontWeight: 700,
        lineHeight: 1.3,
        marginTop: 3,
    },
    subtitle: {
        color: 'var(--dm-muted, #647184)',
        fontSize: '0.8125rem',
        lineHeight: 1.5,
        marginTop: 4,
    },
    readinessPanel: {
        display: 'grid',
        gap: theme.spacing(1.1),
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2.5),
        border: '1px solid var(--dm-border, #dfe6ee)',
        borderRadius: 10,
        backgroundColor: 'var(--dm-row-alt, #f4f7fb)',
    },
    readinessItem: {
        display: 'grid',
        gridTemplateColumns: '24px minmax(0, 1fr)',
        gap: theme.spacing(1),
        alignItems: 'center',
        color: 'var(--dm-text-strong, #344256)',
        fontSize: '0.75rem',
        lineHeight: 1.45,
    },
    successIcon: {
        color: '#2f7d4a',
        fontSize: 20,
    },
    infoIcon: {
        color: '#26709a',
        fontSize: 20,
    },
    overviewGrid: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 0.9fr) minmax(0, 1.1fr)',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2.5),
        [theme.breakpoints.down('md')]: {
            gridTemplateColumns: 'minmax(0, 1fr)',
        },
    },
    card: {
        minWidth: 0,
        padding: theme.spacing(2),
        border: '1px solid var(--dm-border, #dfe6ee)',
        borderRadius: 10,
        backgroundColor: 'var(--contentColor, #ffffff)',
    },
    cardHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1.5),
    },
    cardTitle: {
        color: 'var(--dm-text-strong, #263548)',
        fontSize: '0.875rem',
        fontWeight: 700,
    },
    editButton: {
        minWidth: 0,
        padding: theme.spacing(0.5, 1),
        textTransform: 'none',
    },
    editLinks: {
        display: 'inline-flex',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        gap: theme.spacing(0.5),
    },
    projectLinks: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(0.5),
        marginTop: theme.spacing(1),
        paddingTop: theme.spacing(1),
        borderTop: '1px solid var(--dm-border, #e7ebf0)',
    },
    detailRow: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 0.8fr) minmax(0, 1.2fr)',
        gap: theme.spacing(1),
        padding: theme.spacing(0.75, 0),
        color: 'var(--dm-muted, #647184)',
        fontSize: '0.75rem',
        lineHeight: 1.45,
    },
    detailValue: {
        color: 'var(--dm-text-strong, #263548)',
        fontWeight: 600,
        textAlign: 'right',
        overflowWrap: 'anywhere',
    },
    projectList: {
        display: 'grid',
        gap: theme.spacing(1),
    },
    projectItem: {
        padding: theme.spacing(1.5),
        borderRadius: 8,
        backgroundColor: 'var(--dm-row-alt, #f5f7fa)',
    },
    projectName: {
        color: 'var(--dm-text-strong, #263548)',
        fontSize: '0.8125rem',
        fontWeight: 700,
        lineHeight: 1.4,
        marginBottom: theme.spacing(0.5),
    },
    emptyState: {
        padding: theme.spacing(2),
        color: 'var(--dm-muted, #647184)',
        textAlign: 'center',
        border: '1px dashed var(--dm-border, #d6dde6)',
        borderRadius: 8,
        fontSize: '0.75rem',
    },
    licenceSection: {
        marginBottom: theme.spacing(2.5),
    },
    tableContainer: {
        border: '1px solid var(--dm-border, #dfe6ee)',
        borderRadius: 10,
        backgroundColor: 'var(--contentColor, #ffffff)',
    },
    tableHeaderCell: {
        color: 'var(--dm-muted, #647184)',
        backgroundColor: 'var(--dm-row-alt, #f4f7fb)',
        fontSize: '0.6875rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
    },
    tableCell: {
        color: 'var(--dm-text-strong, #344256)',
        fontSize: '0.75rem',
        borderColor: 'var(--dm-border, #e4e9ef)',
    },
    serviceCell: {
        fontWeight: 600,
    },
    totalRow: {
        '& td': {
            color: theme.palette.primary.main,
            fontWeight: 700,
            backgroundColor: 'var(--dm-row-alt, #f8fafc)',
        },
    },
    footer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        paddingTop: theme.spacing(2.5),
        marginTop: theme.spacing(2.5),
        borderTop: '1px solid var(--dm-border, #dfe6ee)',
        [theme.breakpoints.down('xs')]: {
            alignItems: 'stretch',
            flexDirection: 'column',
        },
    },
    footerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginLeft: 'auto',
        [theme.breakpoints.down('xs')]: {
            '& > button': {
                flex: 1,
            },
        },
    },
    completeButton: {
        minWidth: 190,
    },
    errorState: {
        padding: theme.spacing(4, 2),
        textAlign: 'center',
        color: 'var(--dm-text-strong, #263548)',
        border: '1px solid var(--dm-border, #dfe6ee)',
        borderRadius: 10,
    },
    errorText: {
        color: 'var(--dm-muted, #647184)',
        fontSize: '0.75rem',
        margin: theme.spacing(1, 0, 2),
    },
}));

const isComplete = status => ['complete', 'skipped'].includes(status);

const ProjectSummary = ({
    customerId,
    customer,
    customerLoading,
    onboardingProgress,
    handleEditTab,
    handleBack,
    handleCancel,
    handleCompleteOnboarding,
    completionLoading,
    isExistingCustomer,
}) => {
    const classes = useStyles();
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [review, setReview] = React.useState(null);
    const [reloadKey, setReloadKey] = React.useState(0);

    React.useEffect(() => {
        let cancelled = false;

        const loadReview = async () => {
            if (customerLoading) {
                setLoading(true);
                return;
            }
            if (!customerId) {
                setError('Save the customer before opening Review.');
                setLoading(false);
                return;
            }
            if (!customer) {
                setError('Could not load the customer details required for Review.');
                setLoading(false);
                return;
            }

            setLoading(true);
            setError('');
            try {
                const nextReview = await loadOnboardingReview({
                    customerId,
                    customer,
                    projectServices,
                });

                if (!cancelled) {
                    setReview(nextReview);
                }
            } catch (loadError) {
                if (!cancelled) {
                    setError(loadError?.response?.data?.message || 'Could not load the complete onboarding review.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        loadReview();
        return () => {
            cancelled = true;
        };
    }, [customer, customerId, customerLoading, reloadKey]);

    if (loading) return <AppLoader minHeight={360} />;

    if (error || !review) {
        return (
            <div className={classes.errorState}>
                <Typography component="h2" className={classes.cardTitle}>Review unavailable</Typography>
                <Typography className={classes.errorText}>{error || 'No review data was returned.'}</Typography>
                <Button variant="outlined" color="primary" onClick={() => setReloadKey(key => key + 1)}>
                    Try again
                </Button>
            </div>
        );
    }

    const stepStatuses = (onboardingProgress?.steps || []).reduce((statuses, step) => {
        if (step?.key) statuses[step.key] = step.status;
        return statuses;
    }, {});
    const administrators = review.customer.administrators.length
        ? review.customer.administrators.join(', ')
        : 'Not recorded';
    const readinessItems = [
        {
            success: isComplete(stepStatuses.customer_details) && isComplete(stepStatuses.administrator),
            text: 'Customer details and administrator complete',
        },
        {
            success: isComplete(stepStatuses.licences),
            text: `${review.totals.provisioned} licences provisioned across ${review.serviceCount} services`,
        },
        {
            success: isComplete(stepStatuses.projects),
            text: isComplete(stepStatuses.projects)
                ? `${review.projectCards.length} projects configured`
                : `${review.projectCards.length} projects recorded; project setup is still in progress`,
        },
        {
            success: isComplete(stepStatuses.users),
            text: `${review.uniqueUserCount} users added`,
        },
        {
            success: review.clientPoolRemaining === 0,
            text: review.clientPoolRemaining
                ? `${review.clientPoolRemaining} licences remain unallocated in the customer pool`
                : 'All provisioned licences are allocated to projects',
        },
        {
            success: review.projectLicencesRemaining === 0,
            text: review.projectLicencesRemaining
                ? `${review.projectLicencesRemaining} allocated licences remain unassigned`
                : 'All allocated licences are assigned to users',
        },
    ];

    return (
        <>
            <header className={classes.pageIntro}>
                <Typography className={classes.eyebrow}>Step 8 of 8</Typography>
                <Typography component="h2" className={classes.title}>Review and complete</Typography>
                <Typography className={classes.subtitle}>
                    Check the customer, projects, users, and licence totals before completing onboarding.
                </Typography>
            </header>

            <section className={classes.readinessPanel} aria-label="Onboarding readiness checks">
                {readinessItems.map(item => (
                    <div className={classes.readinessItem} key={item.text}>
                        {item.success
                            ? <CheckCircleIcon className={classes.successIcon} aria-hidden="true" />
                            : <InfoOutlinedIcon className={classes.infoIcon} aria-hidden="true" />}
                        <span>{item.text}</span>
                    </div>
                ))}
            </section>

            <div className={classes.overviewGrid}>
                <section className={classes.card} aria-labelledby="review-customer-heading">
                    <div className={classes.cardHeader}>
                        <Typography component="h3" id="review-customer-heading" className={classes.cardTitle}>Customer</Typography>
                        <span className={classes.editLinks}>
                            <Button
                                type="button"
                                color="primary"
                                size="small"
                                onClick={() => handleEditTab('administrator')}
                                className={classes.editButton}
                            >
                                Administrators
                            </Button>
                            <Button
                                type="button"
                                color="primary"
                                size="small"
                                startIcon={<EditOutlinedIcon fontSize="small" />}
                                onClick={() => handleEditTab('customer')}
                                className={classes.editButton}
                            >
                                Edit
                            </Button>
                        </span>
                    </div>
                    <div className={classes.detailRow}><span>Company</span><span className={classes.detailValue}>{review.customer.name}</span></div>
                    <div className={classes.detailRow}><span>Province</span><span className={classes.detailValue}>{review.customer.province}</span></div>
                    <div className={classes.detailRow}><span>Customer administrator</span><span className={classes.detailValue}>{administrators}</span></div>
                </section>

                <section className={classes.card} aria-labelledby="review-projects-heading">
                    <div className={classes.cardHeader}>
                        <Typography component="h3" id="review-projects-heading" className={classes.cardTitle}>Projects</Typography>
                        <Button
                            type="button"
                            color="primary"
                            size="small"
                            startIcon={<EditOutlinedIcon fontSize="small" />}
                            onClick={() => handleEditTab('project')}
                            className={classes.editButton}
                        >
                            Edit
                        </Button>
                    </div>
                    {review.projectCards.length ? (
                        <div className={classes.projectList}>
                            {review.projectCards.map(project => (
                                <article className={classes.projectItem} key={project.id || project.name}>
                                    <Typography className={classes.projectName}>{project.name}</Typography>
                                    <div className={classes.detailRow}><span>Programme</span><span className={classes.detailValue}>{project.programme}</span></div>
                                    <div className={classes.detailRow}><span>Project administrator</span><span className={classes.detailValue}>{project.administrator}</span></div>
                                    <div className={classes.detailRow}><span>Users</span><span className={classes.detailValue}>{project.users}</span></div>
                                    <div className={classes.detailRow}><span>Licences assigned</span><span className={classes.detailValue}>{project.assignedLicences}</span></div>
                                    <div className={classes.projectLinks} aria-label={`Edit ${project.name}`}>
                                        <Button size="small" color="primary" className={classes.editButton} onClick={() => handleEditTab('project', { projectId: project.id })}>Details</Button>
                                        <Button size="small" color="primary" className={classes.editButton} onClick={() => handleEditTab('users', { projectId: project.id })}>Users</Button>
                                        <Button size="small" color="primary" className={classes.editButton} onClick={() => handleEditTab('assignment', { projectId: project.id })}>Assignment</Button>
                                        <Button size="small" color="primary" className={classes.editButton} onClick={() => handleEditTab('learning_hub', { projectId: project.id })}>Learning Hub</Button>
                                    </div>
                                </article>
                            ))}
                        </div>
                    ) : (
                        <div className={classes.emptyState}>No projects have been recorded.</div>
                    )}
                </section>
            </div>

            <section className={classes.licenceSection} aria-labelledby="review-licences-heading">
                <div className={classes.cardHeader}>
                    <Typography component="h3" id="review-licences-heading" className={classes.cardTitle}>Licences</Typography>
                    <span className={classes.editLinks}>
                        <Button
                            type="button"
                            color="primary"
                            size="small"
                            onClick={() => handleEditTab('allocation')}
                            className={classes.editButton}
                        >
                            Allocation
                        </Button>
                        <Button
                            type="button"
                            color="primary"
                            size="small"
                            startIcon={<EditOutlinedIcon fontSize="small" />}
                            onClick={() => handleEditTab('licences')}
                            className={classes.editButton}
                        >
                            Edit
                        </Button>
                    </span>
                </div>
                <TableContainer className={classes.tableContainer}>
                    <Table size="small" aria-label="Licence totals">
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.tableHeaderCell}>Service</TableCell>
                                <TableCell className={classes.tableHeaderCell} align="right">Provisioned</TableCell>
                                <TableCell className={classes.tableHeaderCell} align="right">Allocated</TableCell>
                                <TableCell className={classes.tableHeaderCell} align="right">Assigned</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {review.serviceRows.map(row => (
                                <TableRow key={row.key}>
                                    <TableCell className={`${classes.tableCell} ${classes.serviceCell}`}>{row.label}</TableCell>
                                    <TableCell className={classes.tableCell} align="right">{row.provisioned}</TableCell>
                                    <TableCell className={classes.tableCell} align="right">{row.allocated}</TableCell>
                                    <TableCell className={classes.tableCell} align="right">{row.assigned}</TableCell>
                                </TableRow>
                            ))}
                            <TableRow className={classes.totalRow}>
                                <TableCell className={classes.tableCell}>Total</TableCell>
                                <TableCell className={classes.tableCell} align="right">{review.totals.provisioned}</TableCell>
                                <TableCell className={classes.tableCell} align="right">{review.totals.allocated}</TableCell>
                                <TableCell className={classes.tableCell} align="right">{review.totals.assigned}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </section>

            <footer className={classes.footer}>
                {!isExistingCustomer && (
                    <Button type="button" color="default" onClick={handleCancel}>Cancel onboarding</Button>
                )}
                <div className={classes.footerActions}>
                    <Button type="button" variant="outlined" color="primary" onClick={handleBack}>Back</Button>
                    <Button
                        type="button"
                        variant="contained"
                        color="primary"
                        onClick={handleCompleteOnboarding}
                        disabled={completionLoading}
                        startIcon={completionLoading ? null : <CheckCircleOutlineIcon fontSize="small" />}
                        className={classes.completeButton}
                    >
                        {completionLoading ? <CircularProgress size={18} color="inherit" /> : 'Complete onboarding'}
                    </Button>
                </div>
            </footer>
        </>
    );
};

export default ProjectSummary;
